import axios, { AxiosHeaders } from 'axios';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://kashround.test';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
});

// Request interceptor: attach XSRF token header for cross-origin Sanctum requests.
// Axios's built-in XSRF handling only works reliably for same-origin requests, so
// we read the cookie explicitly and set the header ourselves.
api.interceptors.request.use((config) => {
  const headers = AxiosHeaders.from(config.headers);

  if (config.data instanceof FormData) {
    // Let the browser set the multipart boundary automatically.
    headers.delete('Content-Type');
  }

  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='));
  if (match) {
    const token = decodeURIComponent(match.slice('XSRF-TOKEN='.length));
    headers.set('X-XSRF-TOKEN', token);
  }

  config.headers = headers;
  return config;
});

// Response interceptor: handle 401 (expired/invalid session)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth-storage');
      const path = globalThis.location.pathname;
      if (
        path !== '/' &&
        !path.startsWith('/login') &&
        !path.startsWith('/register') &&
        !path.startsWith('/forgot-password') &&
        !path.startsWith('/password-reset') &&
        !path.startsWith('/verify-email')
      ) {
        globalThis.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Fetch the CSRF cookie from Sanctum.
 * Must be called before login/register or any state-changing request
 * when no session exists yet.
 */
export async function getCsrfCookie(): Promise<void> {
  await axios.get(`${API_BASE_URL}/sanctum/csrf-cookie`, {
    withCredentials: true,
  });
}

export default api;
